import {
  Avatar,
  Box,
  Divider,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Typography,
} from "@mui/material";
import { Help, Logout, Settings, ChevronRight } from "@mui/icons-material";
import { useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "src/utils/useAuth";

interface UserMenuProps {
  collapsed: boolean;
}

export function UserMenu({ collapsed }: UserMenuProps) {
  const { user, logout } = useAuth();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const openUserMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    handleClose();
    const logoutUrl = window.APP_CONFIG?.logoutUrl;
    // Auth0 logout clears local tokens and server session, then redirects to:
    // - SSO logout URL (if configured) to end the upstream IdP session
    // - App origin (default) if no SSO logout configured
    logout({
      logoutParams: {
        returnTo: logoutUrl || window.location.origin,
      },
    });
  };

  const getInitials = (name: string | undefined) => {
    if (!name) return "?";
    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  return (
    <Box mt={2} mb={2.5}>
      <ListItemButton
        onClick={openUserMenu}
        sx={{
          justifyContent: collapsed ? "center" : "initial",
          px: 2,
          py: 1,
          minHeight: 48,
          maxHeight: 48,
        }}
      >
        <ListItemIcon
          sx={{
            minWidth: "unset",
            justifyContent: "center",
            mr: collapsed ? 0 : 2,
          }}
        >
          <Avatar src={user?.picture} alt={user?.name || "User"} sx={{ width: 32, height: 32 }}>
            {getInitials(user?.name)}
          </Avatar>
        </ListItemIcon>
        {!collapsed && (
          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
            <Typography
              variant="body2"
              sx={{
                fontWeight: 500,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {user?.name || "User"}
            </Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                display: "block",
              }}
            >
              {user?.email || ""}
            </Typography>
          </Box>
        )}
        {!collapsed && (
          <ListItemIcon
            sx={{
              minWidth: "unset",
              justifyContent: "center",
            }}
          >
            <ChevronRight />
          </ListItemIcon>
        )}
      </ListItemButton>

      <Menu
        id="user-menu"
        anchorEl={anchorEl}
        anchorOrigin={{
          horizontal: "right",
          vertical: "bottom",
        }}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        sx={{
          ml: 1.2,
          mt: 1.8,
        }}
      >
        <MenuItem component={Link} to="/settings" onClick={handleClose} sx={{ height: 48, px: 2 }}>
          <ListItemIcon sx={{ minWidth: 32, mr: 2 }}>
            <Settings />
          </ListItemIcon>
          <ListItemText primary="Settings" />
        </MenuItem>
        <MenuItem
          component="a"
          href="https://insight.tveyes.com/help/"
          target="_blank"
          rel="noopener noreferrer"
          onClick={handleClose}
          sx={{ height: 48, px: 2 }}
        >
          <ListItemIcon sx={{ minWidth: 32, mr: 2 }}>
            <Help />
          </ListItemIcon>
          <ListItemText primary="Help" />
        </MenuItem>
        <Divider />
        <MenuItem onClick={handleLogout} sx={{ height: 48, px: 2 }}>
          <ListItemIcon sx={{ minWidth: 32, mr: 2 }}>
            <Logout />
          </ListItemIcon>
          <ListItemText primary="Logout" />
        </MenuItem>
      </Menu>
    </Box>
  );
}
